import React, { useState, useEffect } from 'react';
import { X, Save, Eye, HelpCircle, Church } from 'lucide-react';
import { useChurch } from '../../contexts/ChurchContext';

interface EmailTemplate {
  template_id?: number;
  template_name: string;
  recipient_type: string;
  subject_template: string;
  body_template: string;
  weeks_ahead: number;
  include_song_list: boolean;
  include_metadata: boolean;
  include_video_links: boolean;
  music_folder_paths: string[];
  recipient_emails?: any | null;
  compile_pdf: boolean;
  include_service_music: boolean;
  church_id?: number | null;
}

interface SimpleEmailEditorProps {
  template: EmailTemplate | null;
  onSave: (template: any) => void;
  onCancel: () => void;
}

const SimpleEmailEditor: React.FC<SimpleEmailEditorProps> = ({
  template,
  onSave,
  onCancel
}) => {
  const { userChurches, currentChurch } = useChurch();
  const [formData, setFormData] = useState({
    template_name: '',
    recipient_type: 'admin',
    subject_template: 'Upcoming Music - Next {{weeksAhead}} Weeks',
    email_text: '', // Plain text that will be converted to HTML
    weeks_ahead: 2,
    include_song_list: true,
    include_metadata: true,
    include_video_links: true,
    hide_composer: false,
    hide_lyricist: false,
    hide_video_links: false,
    music_folder_paths: [] as string[],
    compile_pdf: true,
    include_service_music: false,
    church_id: null as number | null,
    recipient_emails: '' // Comma-separated email addresses
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (template) {
      // Convert HTML body back to plain text for editing
      const plainText = htmlToPlainText(template.body_template);
      // Convert recipient_emails array to comma-separated string
      const recipientEmailsStr = Array.isArray(template.recipient_emails)
        ? template.recipient_emails.join(', ')
        : (template.recipient_emails || '');
      setFormData({
        template_name: template.template_name,
        recipient_type: template.recipient_type,
        subject_template: template.subject_template,
        email_text: plainText,
        weeks_ahead: template.weeks_ahead,
        include_song_list: template.include_song_list,
        include_metadata: template.include_metadata,
        include_video_links: template.include_video_links,
        hide_composer: !template.include_metadata,
        hide_lyricist: !template.include_metadata,
        hide_video_links: !template.include_video_links,
        music_folder_paths: template.music_folder_paths || [],
        compile_pdf: template.compile_pdf,
        include_service_music: template.include_service_music,
        church_id: template.church_id || null,
        recipient_emails: recipientEmailsStr
      });
    } else {
      // Set defaults for new template
      setFormData(prev => ({
        ...prev,
        church_id: currentChurch?.id || null
      }));
    }
  }, [template, currentChurch]);

  const htmlToPlainText = (html: string): string => {
    // If it's already plain text (no HTML tags), return as is
    if (!html.includes('<') && !html.includes('>')) {
      return html;
    }
    
    // Create a temporary DOM element to parse HTML properly
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Get plain text and clean up whitespace
    let text = tempDiv.textContent || tempDiv.innerText || '';
    
    // Normalize line breaks and preserve paragraph structure
    text = text.replace(/\s+/g, ' ').trim();
    
    // Add proper line breaks for better formatting
    text = text.replace(/\s*{{songList}}\s*/g, '\n\n{{songList}}\n\n');
    text = text.replace(/Generated on/g, '\n\nGenerated on');
    
    // Clean up excessive whitespace while preserving intentional breaks
    text = text.replace(/\n\s+/g, '\n').replace(/\n{3,}/g, '\n\n');
    
    return text.trim();
  };

  const plainTextToHtml = (text: string, recipientType: string): string => {
    // Get color scheme based on recipient type
    const colors = {
      admin: '#007bff',
      choir: '#6f42c1', 
      band: '#dc3545'
    };
    
    const color = colors[recipientType as keyof typeof colors] || '#333';
    
    // Split text into paragraphs
    const paragraphs = text.split('\n\n').filter(p => p.trim());
    
    // Build HTML with default styling
    let html = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">\n`;
    
    // First paragraph becomes the header
    if (paragraphs.length > 0) {
      html += `  <h1 style="color: ${color}; border-bottom: 2px solid ${color}; padding-bottom: 10px;">${paragraphs[0]}</h1>\n`;
    }
    
    // Remaining paragraphs become body text
    for (let i = 1; i < paragraphs.length; i++) {
      const paragraph = paragraphs[i].trim();
      
      // Check for song list placeholder
      if (paragraph.includes('{{songList}}')) {
        // Split around the song list
        const parts = paragraph.split('{{songList}}');
        if (parts[0].trim()) {
          html += `  <p>${parts[0].trim()}</p>\n`;
        }
        html += `  {{songList}}\n`;
        if (parts[1] && parts[1].trim()) {
          html += `  <p style="margin-top: 30px; color: #666; font-size: 0.9em;">${parts[1].trim()}</p>\n`;
        }
      } else {
        // Regular paragraph
        const style = paragraph.toLowerCase().includes('generated on') || 
                     paragraph.toLowerCase().includes('blessings') ||
                     paragraph.toLowerCase().includes('rock on') ||
                     paragraph.toLowerCase().includes('music ministry')
          ? ' style="margin-top: 30px; color: #666; font-size: 0.9em;"'
          : '';
        html += `  <p${style}>${paragraph}</p>\n`;
      }
    }
    
    html += `</div>`;
    
    return html;
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.template_name.trim()) {
      newErrors.template_name = 'Template name is required';
    }

    if (!formData.subject_template.trim()) {
      newErrors.subject_template = 'Subject is required';
    }

    if (!formData.email_text.trim()) {
      newErrors.email_text = 'Email content is required';
    }

    if (formData.weeks_ahead < 1 || formData.weeks_ahead > 52) {
      newErrors.weeks_ahead = 'Weeks ahead must be between 1 and 52';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    // Convert recipient_emails string to array
    const recipientEmailsArray = formData.recipient_emails
      .split(/[,;]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    // Save as plain text - backend will apply universal styling
    const templateData = {
      template_name: formData.template_name,
      recipient_type: formData.recipient_type,
      subject_template: formData.subject_template,
      body_template: formData.email_text, // Save as plain text
      weeks_ahead: formData.weeks_ahead,
      include_song_list: formData.include_song_list,
      include_metadata: !formData.hide_composer && !formData.hide_lyricist,
      include_video_links: !formData.hide_video_links,
      music_folder_paths: formData.music_folder_paths,
      hide_composer: formData.hide_composer,
      hide_lyricist: formData.hide_lyricist,
      hide_video_links: formData.hide_video_links,
      compile_pdf: formData.compile_pdf,
      include_service_music: formData.include_service_music,
      church_id: formData.church_id,
      recipient_emails: recipientEmailsArray.length > 0 ? recipientEmailsArray : null
    };

    onSave(templateData);
  };

  const generatePreview = (): string => {
    return plainTextToHtml(formData.email_text, formData.recipient_type)
      .replace(/{{songList}}/g, `
        <div style="margin: 20px 0; padding: 15px; background-color: #f8f9fa; border-left: 3px solid #007bff;">
          <h3 style="margin: 0 0 10px 0;">Amazing Grace</h3>
          <p style="margin: 5px 0; color: #666; font-size: 0.9em;">Composer: John Newton | Lyricist: John Newton</p>
          <p style="margin: 10px 0 0 0;"><a href="#" style="color: #007bff;">🎵 Listen on YouTube</a></p>
        </div>
        <div style="margin: 20px 0; padding: 15px; background-color: #f8f9fa; border-left: 3px solid #007bff;">
          <h3 style="margin: 0 0 10px 0;">How Great Thou Art</h3>
          <p style="margin: 5px 0; color: #666; font-size: 0.9em;">Composer: Carl Boberg | Lyricist: Stuart K. Hine</p>
        </div>
      `)
      .replace(/{{weeksAhead}}/g, formData.weeks_ahead.toString())
      .replace(/{{currentDate}}/g, new Date().toLocaleDateString())
      .replace(/{{currentTime}}/g, new Date().toLocaleTimeString());
  };

  const recipientTypes = [
    { value: 'admin', label: 'Admin' },
    { value: 'choir', label: 'Choir' },
    { value: 'band', label: 'Band' },
    { value: 'custom', label: 'Custom' }
  ];

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {template ? 'Edit Email Template' : 'Create Email Template'}
            </h5>
            <button type="button" className="btn-close" onClick={onCancel}></button>
          </div>

          <div className="modal-body">
            <div className="row">
              <div className="col-md-6">
                <h6>Template Settings</h6>
                
                {/* Template Name */}
                <div className="mb-3">
                  <label htmlFor="templateName" className="form-label">
                    Template Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.template_name ? 'is-invalid' : ''}`}
                    id="templateName"
                    value={formData.template_name}
                    onChange={(e) => handleInputChange('template_name', e.target.value)}
                    placeholder="e.g., Weekly Choir Music"
                  />
                  {errors.template_name && (
                    <div className="invalid-feedback">{errors.template_name}</div>
                  )}
                </div>

                {/* Recipient Type */}
                <div className="mb-3">
                  <label htmlFor="recipientType" className="form-label">
                    Recipient Type
                  </label>
                  <select
                    className="form-select"
                    id="recipientType"
                    value={formData.recipient_type}
                    onChange={(e) => handleInputChange('recipient_type', e.target.value)}
                  >
                    {recipientTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Church Selection */}
                {userChurches.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label d-flex align-items-center">
                      <Church size={18} className="me-2" />
                      <strong>Church</strong>
                      <small className="text-muted ms-2">(Template will send music for this church only)</small>
                    </label>
                    <div className="d-flex gap-2 flex-wrap">
                      {userChurches.length > 1 && (
                        <button
                          type="button"
                          className={`btn btn-sm ${formData.church_id === null ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => handleInputChange('church_id', null)}
                        >
                          All Churches
                        </button>
                      )}
                      {userChurches.map((church) => (
                        <button
                          key={church.id}
                          type="button"
                          className={`btn btn-sm ${formData.church_id === church.id ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => handleInputChange('church_id', church.id)}
                        >
                          {church.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Subject */}
                <div className="mb-3">
                  <label htmlFor="subjectTemplate" className="form-label">
                    Email Subject <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.subject_template ? 'is-invalid' : ''}`}
                    id="subjectTemplate"
                    value={formData.subject_template}
                    onChange={(e) => handleInputChange('subject_template', e.target.value)}
                    placeholder="e.g., Upcoming Music - Next {{weeksAhead}} Weeks"
                  />
                  {errors.subject_template && (
                    <div className="invalid-feedback">{errors.subject_template}</div>
                  )}
                </div>

                {/* Default Recipients */}
                <div className="mb-3">
                  <label htmlFor="recipientEmails" className="form-label">
                    Default Recipients
                  </label>
                  <textarea
                    className="form-control"
                    id="recipientEmails"
                    rows={2}
                    value={formData.recipient_emails}
                    onChange={(e) => handleInputChange('recipient_emails', e.target.value)}
                    placeholder="e.g., choir@church.com, director@church.com"
                  />
                  <small className="text-muted">Comma-separated email addresses. These will be pre-filled when sending.</small>
                </div>

                {/* Weeks Ahead */}
                <div className="mb-3">
                  <label htmlFor="weeksAhead" className="form-label">
                    Weeks Ahead
                  </label>
                  <input
                    type="number"
                    className={`form-control ${errors.weeks_ahead ? 'is-invalid' : ''}`}
                    id="weeksAhead"
                    min="1"
                    max="52"
                    value={formData.weeks_ahead}
                    onChange={(e) => handleInputChange('weeks_ahead', parseInt(e.target.value))}
                  />
                  {errors.weeks_ahead && (
                    <div className="invalid-feedback">{errors.weeks_ahead}</div>
                  )}
                </div>

                {/* Song Information Options */}
                <div className="mb-3">
                  <label className="form-label">Song Information</label>
                  <div className="small text-muted mb-2">
                    By default, emails include: Title, Composer, Lyricist, Placement, and Video Links
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="hideComposer"
                      checked={formData.hide_composer}
                      onChange={(e) => handleInputChange('hide_composer', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="hideComposer">
                      Hide composer information
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="hideLyricist"
                      checked={formData.hide_lyricist}
                      onChange={(e) => handleInputChange('hide_lyricist', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="hideLyricist">
                      Hide lyricist information
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="hideVideoLinks"
                      checked={formData.hide_video_links}
                      onChange={(e) => handleInputChange('hide_video_links', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="hideVideoLinks">
                      Hide video links
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="compilePdf"
                      checked={formData.compile_pdf}
                      onChange={(e) => handleInputChange('compile_pdf', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="compilePdf">
                      Compile files into PDF per service
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="includeServiceMusic"
                      checked={formData.include_service_music}
                      onChange={(e) => handleInputChange('include_service_music', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="includeServiceMusic">
                      Include service music (overrides first Sunday only rule)
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6>Email Content</h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    <Eye size={16} className="me-1" />
                    {showPreview ? 'Hide' : 'Show'} Preview
                  </button>
                </div>
                
                {!showPreview ? (
                  <>
                    {/* Email Text */}
                    <div className="mb-3">
                      <label htmlFor="emailText" className="form-label">
                        Email Content <span className="text-danger">*</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0 ms-2"
                          data-bs-toggle="tooltip"
                          title="Write plain text. First paragraph becomes the header. Use {{songList}} where you want songs to appear."
                        >
                          <HelpCircle size={16} />
                        </button>
                      </label>
                      <textarea
                        className={`form-control ${errors.email_text ? 'is-invalid' : ''}`}
                        id="emailText"
                        rows={12}
                        value={formData.email_text}
                        onChange={(e) => handleInputChange('email_text', e.target.value)}
                        placeholder={`Upcoming Music

Dear Team,

Here is the music for the next {{weeksAhead}} weeks:

{{songList}}

Please review and prepare accordingly.

Blessings,
Music Ministry`}
                      />
                      {errors.email_text && (
                        <div className="invalid-feedback">{errors.email_text}</div>
                      )}
                    </div>

                    <div className="alert alert-info small">
                      <strong>Tips:</strong>
                      <ul className="mb-0 mt-1">
                        <li>First paragraph becomes the email header</li>
                        <li>Separate paragraphs with blank lines</li>
                        <li>Use <code>{'{{songList}}'}</code> where songs should appear</li>
                        <li>Use <code>{'{{weeksAhead}}'}</code> for the number of weeks</li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <div className="preview-container">
                    <div 
                      className="border rounded p-3"
                      style={{ height: '400px', overflowY: 'auto', backgroundColor: '#fff' }}
                      dangerouslySetInnerHTML={{ __html: generatePreview() }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              <X size={16} className="me-2" />
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSave}>
              <Save size={16} className="me-2" />
              {template ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleEmailEditor;